import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  RefreshCw, 
  TrendingUp, 
  Building2, 
  DollarSign,
  Users,
  MapPin,
  CheckCircle,
  ChevronRight,
  AlertCircle,
  X,
  User,
  Mail,
  Phone,
  Sparkles,
  Calculator,
  ArrowRight
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/currency';
import { toast } from 'sonner';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';

// Unit Offer Modal with Dynamic IRR Slider
function UnitOfferModal({ listing, brokerId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Dynamic pricing state
  // Slider range: Base value to 1.5x market value
  const minPrice = listing?.originalUnitValue || listing?.currentMarketValue || 0;
  const maxPrice = (listing?.currentMarketValue || minPrice) * 1.5;
  const [offerPrice, setOfferPrice] = useState(listing?.currentMarketValue || minPrice);
  const [economics, setEconomics] = useState(null);
  const [loadingEconomics, setLoadingEconomics] = useState(false);

  // Fetch economics when price changes (debounced)
  const fetchEconomics = useCallback(async (price) => {
    if (!listing?.listingId) return;
    
    setLoadingEconomics(true);
    try {
      const response = await api.get(
        `/broker/secondary-marketplace/listings/${listing.listingId}/irr-preview`,
        { params: { offer_price: price } }
      );
      setEconomics(response.data);
    } catch (error) {
      console.error('Failed to fetch economics:', error);
    } finally {
      setLoadingEconomics(false);
    }
  }, [listing?.listingId]);

  // Debounce the price change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEconomics(offerPrice);
    }, 300);
    return () => clearTimeout(timer);
  }, [offerPrice, fetchEconomics]);

  // Initial load
  useEffect(() => {
    if (listing?.currentMarketValue) {
      setOfferPrice(listing.currentMarketValue);
      fetchEconomics(listing.currentMarketValue);
    }
  }, [listing?.currentMarketValue, fetchEconomics]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSliderChange = (value) => {
    setOfferPrice(value[0]);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.buyerName.trim()) newErrors.buyerName = 'Buyer name is required';
    if (!formData.buyerEmail.trim()) newErrors.buyerEmail = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.buyerEmail)) newErrors.buyerEmail = 'Invalid email';
    if (!formData.buyerPhone.trim()) newErrors.buyerPhone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.buyerPhone.replace(/\D/g, ''))) newErrors.buyerPhone = 'Invalid phone (10 digits)';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await api.post('/broker/secondary-marketplace/offers', {
        listingId: listing.listingId,
        brokerId,
        buyerName: formData.buyerName,
        buyerEmail: formData.buyerEmail,
        buyerPhone: formData.buyerPhone,
        offeredPrice: offerPrice,
        notes: formData.notes
      });
      onSuccess(economics);
    } catch (error) {
      console.error('Failed to submit offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit offer');
    } finally {
      setSubmitting(false);
    }
  };

  const hasBonus = economics?.performanceBonus > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="offer-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#0A0A0A] border border-white/10 rounded-sm shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="offer-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#0A0A0A] to-[#1A1A1A] border-b border-white/10 px-6 py-4">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold text-white" data-testid="modal-unit-name">{listing?.unitName}</h2>
                <p className="text-white/50 text-sm">{listing?.projectName}</p>
                <div className="flex items-center gap-1 mt-1 text-sm text-white/40">
                  <MapPin className="h-3 w-3" />
                  {listing?.location}
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-sm transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="h-5 w-5 text-white/50" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Buyer Outflow Section - Key Info */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
              <p className="text-xs text-blue-400 uppercase tracking-wider mb-3">BUYER OUTFLOW (at current offer)</p>
              {loadingEconomics ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                </div>
              ) : economics ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/60">Capital Deployed by Investors</span>
                    <span className="font-mono text-white">{formatINR(economics.capitalDeployed)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/60">+ Premium (Appreciation)</span>
                    <span className="font-mono text-green-400">+{formatINR(economics.premium)}</span>
                  </div>
                  <div className="border-t border-blue-500/20 pt-2 flex justify-between items-center">
                    <span className="font-semibold text-blue-400">BUYER PAYS</span>
                    <span className="text-lg font-mono text-blue-400 font-bold" data-testid="buyer-pays">
                      {formatINR(economics.buyerPays)}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Price Slider Section */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-5" data-testid="price-slider-section">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="h-5 w-5 text-[#4ADE80]" />
                <h3 className="font-semibold text-white">OFFER PRICE (Unit Valuation)</h3>
              </div>
              
              <div className="mb-6">
                <Slider
                  value={[offerPrice]}
                  min={minPrice}
                  max={maxPrice}
                  step={100000}
                  onValueChange={handleSliderChange}
                  className="py-4"
                  data-testid="price-slider"
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                  <span>{formatINR(minPrice)}</span>
                  <span className="text-[#4ADE80] text-lg font-mono" data-testid="current-offer-price">
                    {formatINR(offerPrice)}
                  </span>
                  <span>{formatINR(maxPrice)}</span>
                </div>
              </div>

              {/* Earnings Display */}
              <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-sm p-4" data-testid="earnings-display">
                <p className="text-xs text-[#4ADE80]/60 uppercase tracking-wider mb-3">YOUR EARNINGS</p>
                
                {loadingEconomics ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-[#4ADE80]/30 border-t-[#4ADE80] rounded-full animate-spin" />
                  </div>
                ) : economics ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-white/60">Brokerage (1.5%)</span>
                      <span className="font-mono text-white" data-testid="brokerage-amount">
                        {formatINR(economics.brokerage)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/60 flex items-center gap-2">
                        Performance Bonus 
                        {hasBonus && <Sparkles className="h-4 w-4 text-amber-400 animate-pulse" />}
                      </span>
                      <span className={`font-mono ${hasBonus ? 'text-amber-400' : 'text-white/40'}`} data-testid="bonus-amount">
                        {formatINR(economics.performanceBonus)}
                      </span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between items-center">
                      <span className="font-semibold text-white">TOTAL EARNINGS</span>
                      <span className="text-xl font-mono text-[#4ADE80] font-bold" data-testid="total-earnings">
                        {formatINR(economics.totalEarnings)}
                      </span>
                    </div>
                    {hasBonus && (
                      <p className="text-xs text-amber-400/70 pt-2">
                        ✨ Bonus earned from {economics.investorsWithBonus} investor{economics.investorsWithBonus > 1 ? 's' : ''} exceeding 15% IRR hurdle
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Dynamic Auto-Accept Status - Based on offer price */}
            <div className={`p-4 rounded-sm border ${
              economics?.canAutoAccept 
                ? 'bg-green-500/10 border-green-500/20' 
                : 'bg-amber-500/10 border-amber-500/20'
            }`}>
              <div className="flex items-start gap-3">
                {economics?.canAutoAccept ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-400">Platform Can Accept</p>
                      <p className="text-sm text-green-400/70">
                        Offer is at {economics?.appreciationPA?.toFixed(1)}% p.a. appreciation (≥5% threshold).
                        Sale can proceed without investor consent.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-400">Requires Investor Vote</p>
                      <p className="text-sm text-amber-400/70">
                        Offer is at {economics?.appreciationPA?.toFixed(1)}% p.a. appreciation (&lt;5% threshold).
                        All investors must vote to approve this sale.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Buyer Details Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="font-semibold text-white">Buyer Details</h3>
              
              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  <User className="h-4 w-4 inline mr-1" />
                  Buyer Name *
                </label>
                <Input
                  name="buyerName"
                  value={formData.buyerName}
                  onChange={handleChange}
                  placeholder="Enter buyer's full name"
                  className={`bg-white/5 border-white/10 text-white ${errors.buyerName ? 'border-red-500' : ''}`}
                  data-testid="buyer-name-input"
                />
                {errors.buyerName && <p className="text-red-400 text-xs mt-1">{errors.buyerName}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="buyerEmail"
                    value={formData.buyerEmail}
                    onChange={handleChange}
                    placeholder="buyer@email.com"
                    className={`bg-white/5 border-white/10 text-white ${errors.buyerEmail ? 'border-red-500' : ''}`}
                    data-testid="buyer-email-input"
                  />
                  {errors.buyerEmail && <p className="text-red-400 text-xs mt-1">{errors.buyerEmail}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    <Phone className="h-4 w-4 inline mr-1" />
                    Phone *
                  </label>
                  <Input
                    name="buyerPhone"
                    value={formData.buyerPhone}
                    onChange={handleChange}
                    placeholder="10-digit number"
                    className={`bg-white/5 border-white/10 text-white ${errors.buyerPhone ? 'border-red-500' : ''}`}
                    data-testid="buyer-phone-input"
                  />
                  {errors.buyerPhone && <p className="text-red-400 text-xs mt-1">{errors.buyerPhone}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/60 mb-1">
                  Additional Notes
                </label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any additional information..."
                  rows={2}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="notes-input"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 h-12"
                  data-testid="submit-offer-btn"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                  ) : (
                    <>
                      <DollarSign className="h-5 w-5 mr-2" />
                      Submit Offer
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="h-12 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Main SecondaryMarketplace Component
export default function SecondaryMarketplace() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // My Offers tab
  const [activeTab, setActiveTab] = useState('marketplace');
  const [myOffers, setMyOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  const brokerId = localStorage.getItem('userId');

  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    if (activeTab === 'my-offers') {
      fetchMyOffers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/broker/secondary-marketplace/listings');
      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
      toast.error('Failed to load listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyOffers = async () => {
    setLoadingOffers(true);
    try {
      const response = await api.get(`/broker/${brokerId}/secondary-offers`);
      setMyOffers(response.data.offers || []);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoadingOffers(false);
    }
  };

  const openOfferModal = (listing) => {
    setSelectedListing(listing);
    setShowOfferModal(true);
  };

  const handleOfferSuccess = (economics) => {
    setShowOfferModal(false);
    setSelectedListing(null);
    fetchListings();
    toast.success(
      <div>
        <p className="font-semibold">Offer Submitted!</p>
        <p className="text-sm">Potential earnings: {formatINR(economics?.totalEarnings || 0)}</p>
      </div>
    );
  };

  const filteredListings = listings.filter(listing => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        listing.unitName?.toLowerCase().includes(query) ||
        listing.projectName?.toLowerCase().includes(query) ||
        listing.location?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const offerStatusColors = {
    PENDING: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    ACCEPTED: 'bg-green-500/20 text-green-400 border-green-500/30',
    REJECTED: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <div className="flex min-h-screen bg-black">
      <BrokerSidebar />
      
      <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-light text-white mb-2">Secondary Market</h1>
            <p className="text-sm text-white/40">
              Find buyers for entire units. Earn brokerage + performance bonus on high-value deals.
            </p>
          </div>
          <Button 
            onClick={fetchListings} 
            variant="outline" 
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10">
          <button
            onClick={() => setActiveTab('marketplace')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'marketplace'
                ? 'text-[#4ADE80] border-[#4ADE80]'
                : 'text-white/50 border-transparent hover:text-white'
            }`}
            data-testid="marketplace-tab"
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            Available Units
          </button>
          <button
            onClick={() => setActiveTab('my-offers')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'my-offers'
                ? 'text-[#4ADE80] border-[#4ADE80]'
                : 'text-white/50 border-transparent hover:text-white'
            }`}
            data-testid="my-offers-tab"
          >
            <DollarSign className="h-4 w-4 inline mr-2" />
            My Offers
            {myOffers.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/10 rounded-full text-xs">
                {myOffers.length}
              </span>
            )}
          </button>
        </div>

        {/* Search (marketplace tab only) */}
        {activeTab === 'marketplace' && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                placeholder="Search by unit, project, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white"
                data-testid="search-input"
              />
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'marketplace' ? (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Available Units</p>
                <p className="text-2xl font-mono text-white" data-testid="stat-available">{listings.length}</p>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Auto-Accept Ready</p>
                <p className="text-2xl font-mono text-green-400" data-testid="stat-autoaccept">
                  {listings.filter(l => l.autoAcceptEligible).length}
                </p>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Total Value</p>
                <p className="text-2xl font-mono text-white" data-testid="stat-value">
                  {formatINR(listings.reduce((sum, l) => sum + (l.currentMarketValue || 0), 0))}
                </p>
              </div>
              
              <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-sm p-4">
                <p className="text-xs text-[#4ADE80]/60 uppercase tracking-wider mb-1">Potential Brokerage</p>
                <p className="text-2xl font-mono text-[#4ADE80]" data-testid="stat-brokerage">
                  {formatINR(listings.reduce((sum, l) => sum + (l.potentialBrokerage || 0), 0))}
                </p>
              </div>
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#4ADE80]/30 border-t-[#4ADE80] rounded-full animate-spin" />
              </div>
            ) : filteredListings.length === 0 ? (
              <div className="text-center py-20 border border-white/10 rounded-sm">
                <Building2 className="h-12 w-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No units available for sale</p>
                <p className="text-sm text-white/30 mt-1">Check back later for new opportunities</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredListings.map((listing) => (
                  <motion.div
                    key={listing.listingId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`listing-card-${listing.listingId}`}
                  >
                    <Card 
                      className="overflow-hidden bg-white/5 border-white/10 hover:border-[#4ADE80]/30 transition-all cursor-pointer group"
                      onClick={() => openOfferModal(listing)}
                    >
                      {/* Header */}
                      <div className="relative bg-gradient-to-r from-[#1A1A1A] to-[#0A0A0A] text-white p-4">
                        <div className="absolute top-3 right-3">
                          {listing.autoAcceptEligible ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-sm flex items-center gap-1 border border-green-500/30">
                              <CheckCircle className="h-3 w-3" />
                              Auto-Accept
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-sm border border-amber-500/30">
                              Needs Consent
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-lg pr-24">{listing.unitName}</h3>
                        <p className="text-white/50 text-sm">{listing.projectName}</p>
                        <div className="flex items-center gap-1 mt-2 text-sm text-white/40">
                          <MapPin className="h-3 w-3" />
                          {listing.location}
                        </div>
                      </div>

                      <CardContent className="p-4">
                        {/* Key Metrics - Clarified for broker */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <p className="text-xs text-white/40">Unit Value</p>
                            <p className="font-semibold text-white">{formatINR(listing.originalUnitValue)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-white/40">Appreciation</p>
                            <p className="font-semibold flex items-center gap-1 text-green-400">
                              <TrendingUp className="h-4 w-4" />
                              {listing.appreciationPercent?.toFixed(1)}% (+{formatINR(listing.premium)})
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-white/40">Capital Deployed</p>
                            <p className="font-semibold text-white">{formatINR(listing.capitalDeployed)}</p>
                          </div>
                          <div className="bg-blue-500/10 rounded-sm p-2 -m-1">
                            <p className="text-xs text-blue-400">BUYER PAYS</p>
                            <p className="font-semibold text-blue-400">{formatINR(listing.buyerPaysForStakes)}</p>
                          </div>
                        </div>
                        
                        {/* Brokerage highlight */}
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-3 mb-4">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-white/60">Your Brokerage (1.5%)</span>
                            <span className="font-semibold text-amber-400">{formatINR(listing.potentialBrokerage)}</span>
                          </div>
                        </div>

                        {/* Next Milestone */}
                        {listing.nextMilestone && listing.remainingAmount > 0 && (
                          <div className="flex items-center gap-2 text-sm text-white/40 mb-4 p-2 bg-white/5 rounded-sm">
                            <Users className="h-4 w-4" />
                            <span>Buyer assumes: {listing.nextMilestone} ({formatINR(listing.remainingAmount)} remaining)</span>
                          </div>
                        )}

                        {/* CTA */}
                        <Button className="w-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 group-hover:shadow-lg group-hover:shadow-[#4ADE80]/20 transition-all">
                          I Have a Buyer
                          <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* My Offers Tab */
          <div className="space-y-4">
            {loadingOffers ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#4ADE80]/30 border-t-[#4ADE80] rounded-full animate-spin" />
              </div>
            ) : myOffers.length === 0 ? (
              <div className="text-center py-20 border border-white/10 rounded-sm">
                <DollarSign className="h-12 w-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">You have not submitted any offers yet</p>
                <Button 
                  className="mt-4 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
                  onClick={() => setActiveTab('marketplace')}
                >
                  Browse Available Units
                </Button>
              </div>
            ) : (
              myOffers.map((offer) => (
                <motion.div
                  key={offer.offerId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/5 border border-white/10 rounded-sm p-5"
                  data-testid={`my-offer-${offer.offerId}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-white">{offer.unitName || 'Unit'}</h3>
                      <p className="text-sm text-white/50">Buyer: {offer.buyerName}</p>
                      <p className="text-sm text-white/50">Offered: {formatINR(offer.offeredPrice)}</p>
                      <p className="text-xs text-white/30 mt-1">
                        Submitted: {new Date(offer.submittedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-sm text-xs font-medium border ${offerStatusColors[offer.status]}`}>
                      {offer.status}
                    </span>
                  </div>
                  {offer.status === 'ACCEPTED' && (
                    <div className="mt-3 p-3 bg-green-500/10 rounded-sm border border-green-500/20">
                      <p className="text-sm text-green-400">
                        🎉 Your brokerage: {formatINR(offer.brokerageAmount)}
                      </p>
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Offer Modal */}
      {showOfferModal && selectedListing && (
        <UnitOfferModal
          listing={selectedListing}
          brokerId={brokerId}
          onClose={() => {
            setShowOfferModal(false);
            setSelectedListing(null);
          }}
          onSuccess={handleOfferSuccess}
        />
      )}
    </div>
  );
}
